import { PayerAdjustmentGroupType } from '../../api';
import { AdjudicationCode, formatCodeChip } from './denial-code';

/**
 * Adjustments that are routine on a PAID claim — hidden from the denials table by default.
 *
 * ## Why this exists
 *
 * `getAdjustmentReport` counts every CAS adjustment on every 835, and some of those are not denials at all:
 *
 * - `CO-45` — charge exceeds the fee schedule / contracted amount. On essentially every in-network paid claim:
 *   it is the contractual write-off.
 * - `PR-1`, `PR-2`, `PR-3` — deductible, coinsurance, copay. The patient's share of a claim the payer PAID.
 *
 * Left in, CO-45 tops a table on a page titled "Denials" at any practice with real remits.
 *
 * ## Why it is this short — read before adding a code
 *
 * Settled with Vadim 2026-09-11: hide exactly these four by default, behind a visible toggle that names them.
 * Every code on this list is a claim that the code is not a denial, and CO-45 already strains that — it is the
 * code an underpayment review keys on, which is why the page keeps it one click away instead of removing it.
 * Growing the list needs the same conversation.
 *
 * Matched on group AND reason code: `PR-45` and `CO-1` are not routine. A remark code does not change it.
 */
export const ROUTINE_ADJUSTMENTS: ReadonlyArray<{ group: PayerAdjustmentGroupType; reasonCode: string }> = [
    { group: PayerAdjustmentGroupType.CONTRACTUALOBLIGATIONS, reasonCode: '45' },
    { group: PayerAdjustmentGroupType.PATIENTRESPONSIBILITY, reasonCode: '1' },
    { group: PayerAdjustmentGroupType.PATIENTRESPONSIBILITY, reasonCode: '2' },
    { group: PayerAdjustmentGroupType.PATIENTRESPONSIBILITY, reasonCode: '3' },
];

/** The `kind` check narrows the union — a rejection has no group or reason code to compare. */
export const isRoutineAdjustment = (code: AdjudicationCode): boolean =>
    code.kind === 'DENIAL' && ROUTINE_ADJUSTMENTS.some(r => r.group === code.group && r.reasonCode === code.reasonCode);

/** `CO-45, PR-1, PR-2, PR-3` — the exact list, for the control that hides them. Derived, never re-typed. */
export const ROUTINE_ADJUSTMENT_LABEL = ROUTINE_ADJUSTMENTS.map(r =>
    formatCodeChip({ kind: 'DENIAL', group: r.group, reasonCode: r.reasonCode }),
).join(', ');
